// App shell: navigation sidebar + top bar with the current partner and logout.
// On phones the sidebar becomes an off-canvas drawer toggled from the topbar
// (see responsive.css); tapping a link or the scrim closes it.
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import he from '../i18n/he';
import { useAuth } from '../context/AuthContext';
import { useAlerts } from '../context/AlertsContext';
import Button from './Button';
import NotificationBell from './NotificationBell';
import { navItemsFor } from './navItems';
import NavIcon from './navIcons';
import Logo from './Logo';
import ThemeSwitch from './ThemeSwitch';


// A dot, not a number: the sidebar's job is to say "there is something here", and the
// count belongs to the page itself. Red for late, amber for due today — the same two
// colours the bell uses for the same two states, so one vocabulary covers both. On the
// money item amber means work waiting rather than something due today, so its title
// says that instead.
const dotTitle = (alertKey, level) => {
  if (level === 'overdue') return he.alerts.dotOverdue;
  if (level === 'waiting') return he.alerts.dotMoneyWaiting;
  return alertKey === 'money' ? he.alerts.dotMoneyToDo : he.alerts.dotToday;
};

function NavDot({ alertKey, level }) {
  if (!level) return null;
  return <span className={`nav-dot nav-dot-${level}`} title={dotTitle(alertKey, level)} />;
}

export default function Layout({ children }) {
  const { partner, logout } = useAuth();
  const { navLevel } = useAlerts();

  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);
  return (
    <div className={`app${navOpen ? ' nav-open' : ''}`}>
      {navOpen && <div className="scrim" onClick={closeNav} />}
      <aside className="sidebar">
        <div className="brand"><Logo /></div>
        {/* Cosmetic only — see navItems.js. The server refuses on every request; this
            just keeps a content writer from being shown a wall of links that all
            answer 403. */}
        {navItemsFor(partner.role).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={closeNav}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>{item.label}</span>
            {item.alert && <NavDot alertKey={item.alert} level={navLevel(item.alert)} />}
            {/* Last in the DOM so the label stays span:first-child (alerts.css); drawn first. */}
            <NavIcon to={item.to} />
          </NavLink>

        ))}
      </aside>
      <div className="main">
        <div className="topbar">
          <button
            type="button"
            className="nav-toggle"
            aria-label={he.nav.menu}
            onClick={() => setNavOpen((o) => !o)}
          >
            ☰
          </button>
          {/* The sidebar carries the logo; on a phone it is a closed drawer, so the top bar does. */}
          <Logo className="topbar-logo" size={26} />
          <div className="row">
            <ThemeSwitch />
            <NotificationBell />
            <span className="who-avatar" aria-hidden>{String(partner.name || '').trim().charAt(0)}</span>
            <span className="who">{partner.name}</span>

            <Button variant="subtle" small onClick={logout}>
              {he.nav.logout}
            </Button>
          </div>
        </div>
        <div className="content">{children}</div>
      </div>
    </div>
  );
}
